import React, { useEffect, useState } from 'react';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Hero from './components/sections/Hero';
import About from './components/sections/About';
import Experience from './components/sections/Experience';
import Projects from './components/sections/Projects';
import Widgets from './components/sections/Widgets';
import Contact from './components/sections/Contact';
import Posts from './pages/Posts';
import Admin from './pages/Admin';
import useKonamiCode from './hooks/useKonamiCode';
import KonamiEffects from './components/KonamiEffects';
import analytics from './services/analytics';

function App() {
  const { coffeeRainActive, snowThemeActive } = useKonamiCode();
  const [currentPage, setCurrentPage] = useState('home');

  useEffect(() => {
    // Handle hash-based routing
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === 'posts') {
        setCurrentPage('posts');
      } else if (hash === 'admin') {
        setCurrentPage('admin');
      } else {
        setCurrentPage('home');
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (currentPage === 'home') {
      // Intersection Observer for scroll animations and section tracking
      const observerOptions = {
        threshold: 0.15,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Add animation class with slight delay for smoother effect
            setTimeout(() => {
              entry.target.classList.add('animate-fade-in');
            }, 50);
            
            // Track section view
            if (entry.target.id) {
              analytics.trackSectionView(entry.target.id);
            }
          }
        });
      }, observerOptions);

      // Observe all sections
      document.querySelectorAll('section').forEach(section => {
        observer.observe(section);
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [currentPage]);

  return (
    <div className="relative min-h-[100dvh]">
      {/* Konami Code Easter Eggs */}
      <KonamiEffects
        coffeeRainActive={coffeeRainActive}
        snowThemeActive={snowThemeActive}
      />
      
      {/* Navigation */}
      {currentPage === 'home' && <Navigation />}
      
      {/* Main Content */}
      <main className="relative z-10">
        {currentPage === 'home' ? (
          <>
            <Hero />
            <About />
            <Experience />
            <Projects />
            <Widgets />
            <Contact />
          </>
        ) : currentPage === 'posts' ? (
          <Posts onBack={() => {
            window.location.hash = '';
            setCurrentPage('home');
          }} />
        ) : currentPage === 'admin' ? (
          <Admin onBack={() => {
            window.location.hash = '';
            setCurrentPage('home');
          }} />
        ) : null}
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* Scroll to Top Button */}
      {currentPage === 'home' && <ScrollToTop />}
    </div>
  );
}

export default App;
